import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Check, Sparkles, Lock, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import api from '../lib/api';
import { sortLibraryDocs } from '../lib/sortLibraryDocs';
import { shouldRefetchOnRefocus } from '../hooks/refocusThrottle';
import { ListSkeleton } from '../components/skeletons';

// Status pill colors — mirror the dashboard library cards (do NOT introduce new palette).
// #154: recibe la CLAVE derivada, no `doc.status` a secas — "en fila" no es un status de
// Mongo sino un estado que sale de la ausencia del sello (ver DocRow). El tono es el MISMO
// que el Panel le dio a su gemelo (Dashboard.js statusColor), no uno nuevo.
function statusStyle(statusKey) {
  if (statusKey === 'processing') return 'bg-amber-500/15 text-amber-400 border-amber-500/20';
  if (statusKey === 'queued') return 'bg-amber-500/10 text-amber-300 border-amber-500/20';
  if (statusKey === 'error') return 'bg-red-500/15 text-red-400 border-red-500/20';
  return 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20'; // ready / partial
}

function formatDate(iso, lng) {
  try {
    return new Intl.DateTimeFormat(lng || 'en', { year: 'numeric', month: 'long', day: 'numeric' })
      .format(new Date(iso));
  } catch {
    return iso || '';
  }
}

/**
 * "Choose which documents stay active on downgrade" screen (/manage-documents).
 *
 * Reads GET /payments/keep-selection (docs in dashboard order, saved keep_doc_ids, destination
 * tope max_docs, locked) + GET /payments/downgrade-notice (for the change date). Auto-saves every
 * toggle via PUT /payments/keep-selection (no Save button); reverts + toasts on failure. When
 * locked (boundary passed / plan already changed) the whole screen is read-only.
 *
 * Does not touch the dashboard active/locked toggle, the banner, or the upgrade flow.
 */
export default function ManageDocuments() {
  const { t, i18n } = useTranslation(['dashboard', 'common']);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [docs, setDocs] = useState([]);
  const [maxDocs, setMaxDocs] = useState(0);
  const [locked, setLocked] = useState(true);
  const [changeDate, setChangeDate] = useState(null);
  // Selection as an ORDERED list of doc ids (the order the user picked them).
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  // Guards against a stale toggle racing a slower save: only the latest PUT may revert.
  const saveSeq = useRef(0);
  // Last fetch timestamp — throttles the on-focus/visibility re-fetch (same as useDowngradeNotice).
  const lastFetchRef = useRef(0);

  // silent=true is a background refresh (on-focus / boundary timer): it must NOT flash the
  // full-screen spinner, and a transient error must NOT wipe the user's in-progress view.
  const load = useCallback(async (silent = false) => {
    lastFetchRef.current = Date.now();
    if (!silent) setLoading(true);
    try {
      const [selRes, noticeRes] = await Promise.all([
        api.get('/payments/keep-selection'),
        api.get('/payments/downgrade-notice').catch(() => ({ data: { show: false } })),
      ]);
      const data = selRes.data || {};
      const list = Array.isArray(data.docs) ? data.docs : [];
      setDocs(list);
      setMaxDocs(Number(data.max_docs) || 0);
      // Server recomputes `locked` from boundary_passed on every call, so a background
      // re-fetch is exactly what flips the screen to read-only once the boundary passes.
      setLocked(Boolean(data.locked));
      // Keep only ids that are still present, in the saved (selection) order.
      const present = new Set(list.map(d => d.id));
      setSelected((data.keep_doc_ids || []).map(String).filter(id => present.has(id)));
      setChangeDate(noticeRes?.data?.show ? noticeRes.data.change_date : null);
    } catch {
      // Fail-safe only on the initial (non-silent) load -> the neutral "nothing to manage"
      // state. A background error keeps whatever the user is currently looking at.
      if (!silent) { setDocs([]); setSelected([]); setMaxDocs(0); setLocked(true); setChangeDate(null); }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // (idle net, part a) Re-fetch on tab focus/visibility (throttled) so the screen catches up
  // to a plan change that happened server-side while the user sat here — the downgrade boundary
  // is fired by the Stripe webhook and does NOT broadcast a planSync signal, so this is the net.
  useEffect(() => {
    const onRefocus = () => {
      if (shouldRefetchOnRefocus(Date.now(), lastFetchRef.current, document.visibilityState)) load(true);
    };
    window.addEventListener('focus', onRefocus);
    document.addEventListener('visibilitychange', onRefocus);
    return () => {
      window.removeEventListener('focus', onRefocus);
      document.removeEventListener('visibilitychange', onRefocus);
    };
  }, [load]);

  // (idle net, part b) Single timer toward period_end (changeDate): re-fetch just after the
  // boundary so a user who never changes focus still flips to read-only. No background interval.
  useEffect(() => {
    if (!changeDate) return;                       // no period_end -> on-focus is the only net
    const ms = new Date(changeDate).getTime() - Date.now();
    if (isNaN(ms) || ms <= 0) return;              // already passed -> load() already reflects it
    // +1s past the boundary; clamp to setTimeout's 32-bit ceiling (far-future is covered by focus).
    const delay = Math.min(ms + 1000, 2147483647);
    const id = setTimeout(() => { load(true); }, delay);
    return () => clearTimeout(id);
  }, [changeDate, load]);

  const docsById = useMemo(() => {
    const m = {};
    for (const d of docs) m[d.id] = d;
    return m;
  }, [docs]);

  // Dashboard order: newest-first, split parts grouped together (same util the library uses).
  const dashboardOrder = useMemo(() => sortLibraryDocs(docs), [docs]);

  // LIVE preview of the boundary sweep — replicates services/document_archive._archive_overflow
  // (lines 90-104) so the labels never lie. Of the docs the sweep actually partitions (status in
  // ACTIVE_STATUSES, strict per-doc newest-first by created_at), the chosen ones are kept, the
  // remaining room (N - chosen) is FILLED with the newest non-chosen, and the rest overflow. Only
  // ready/error/partial overflow is REALLY archived: processing in overflow is skipped (stays
  // active), and docs the sweep never touches (e.g. status 'uploaded') always stay active too.
  // -> lockedIds = the set of docs that would genuinely be locked. Everything else stays active.
  const lockedIds = useMemo(() => {
    const N = maxDocs;
    if (!N) return new Set();
    const ACTIVE = new Set(['ready', 'processing', 'error', 'partial']); // the sweep's `active` set
    const ARCHIVABLE = new Set(['ready', 'error', 'partial']);           // what actually archives
    const sweepable = docs.filter(d => ACTIVE.has(d.status));
    if (sweepable.length <= N) return new Set();                         // backend: nothing to archive
    // Strict per-doc newest-first (the sweep does NOT group split parts). Tie-break: id desc as a
    // best-effort stand-in for the backend's _id desc (the client isn't sent _id — see report).
    const sorted = [...sweepable].sort((a, b) => {
      const ca = a.created_at || '', cb = b.created_at || '';
      if (ca !== cb) return ca < cb ? 1 : -1;
      const ia = a.id || '', ib = b.id || '';
      return ia < ib ? 1 : ia > ib ? -1 : 0;
    });
    const sel = new Set(selected);
    const keptIds = new Set(sorted.filter(d => sel.has(d.id)).slice(0, N).map(d => d.id));
    const room = N - keptIds.size;
    if (room > 0) sorted.filter(d => !keptIds.has(d.id)).slice(0, room).forEach(d => keptIds.add(d.id));
    const locked = new Set();
    for (const d of sorted) if (!keptIds.has(d.id) && ARCHIVABLE.has(d.status)) locked.add(d.id);
    return locked;
  }, [docs, selected, maxDocs]);

  // Two visual groups. "Stay active" = everything NOT really locked, split into the user's explicit
  // picks (selection order) and the rest kept automatically (dashboard order). "Locked" = only the
  // docs the sweep would actually archive.
  const chosenList = useMemo(
    () => selected.map(id => docsById[id]).filter(Boolean),
    [selected, docsById]
  );
  const autoList = useMemo(
    () => dashboardOrder.filter(d => !selected.includes(d.id) && !lockedIds.has(d.id)),
    [dashboardOrder, selected, lockedIds]
  );
  const blockedList = useMemo(
    () => dashboardOrder.filter(d => lockedIds.has(d.id)),
    [dashboardOrder, lockedIds]
  );

  const atMax = maxDocs > 0 && selected.length >= maxDocs;

  const persist = useCallback(async (next, prev) => {
    const seq = ++saveSeq.current;
    setSaving(true);
    try {
      await api.put('/payments/keep-selection', { keep_doc_ids: next });
      if (seq === saveSeq.current) toast.success(t('dashboard:manage_page.saved'));
    } catch (e) {
      // Revert ONLY if this is still the latest change the user made.
      if (seq === saveSeq.current) {
        setSelected(prev);
        // Boundary passed mid-edit: the backend rejects with 409 selection_locked. Flip the
        // whole screen to read-only ("your plan already changed") instead of a generic error.
        if (e?.response?.data?.detail?.code === 'selection_locked') {
          setLocked(true);
        } else {
          toast.error(e?.response?.data?.detail?.message || t('dashboard:manage_page.save_failed'));
        }
      }
    } finally {
      if (seq === saveSeq.current) setSaving(false);
    }
  }, [t]);

  const toggle = useCallback((id) => {
    if (locked) return;
    const isSel = selected.includes(id);
    if (!isSel && atMax) {
      toast.message(t('dashboard:manage_page.max_reached', { max: maxDocs }));
      return;
    }
    const prev = selected;
    const next = isSel ? selected.filter(x => x !== id) : [...selected, id];
    setSelected(next);
    persist(next, prev);
  }, [locked, selected, atMax, maxDocs, persist, t]);

  const lng = i18n.language;

  // ── Loading ── Silueta, no spinner (F4.3b). Esta pantalla NO usa el cache de
  // pantallas a propósito: `locked` se recomputa en el server en cada carga y
  // pintar un estado editable cacheado que ya venció sería una pantalla que
  // miente — aquí siempre fetch fresco + skeleton.
  if (loading) {
    return (
      <div className="max-w-2xl mx-auto py-8" data-testid="manage-docs-loading">
        <ListSkeleton rows={4} testid="skeleton-manage-docs" />
      </div>
    );
  }

  // ── Neutral state: nothing scheduled / nothing to manage ──
  if (locked && selected.length === 0) {
    return (
      <div className="max-w-2xl mx-auto space-y-6 animate-fade-in-up" data-testid="manage-docs-empty">
        <BackHeader navigate={navigate} t={t} />
        <div className="glass-card rounded-[20px] p-6 md:p-8 text-center space-y-2">
          <h1 className="text-lg font-semibold text-white">{t('dashboard:manage_page.empty_title')}</h1>
          <p className="text-sm text-[#a0a0a0]">{t('dashboard:manage_page.empty_body')}</p>
        </div>
      </div>
    );
  }

  const subtitle = changeDate
    ? t('dashboard:manage_page.subtitle', { change_date: formatDate(changeDate, lng), max: maxDocs })
    : t('dashboard:manage_page.subtitle_no_date', { max: maxDocs });

  return (
    <div className="max-w-2xl mx-auto space-y-5 animate-fade-in-up" data-testid="manage-docs-page">
      <BackHeader navigate={navigate} t={t} />

      {/* Heading + subtitle + live counter */}
      <div className="space-y-2">
        <h1 className="text-xl md:text-2xl font-bold text-white">
          {t(locked ? 'dashboard:manage_page.locked_title' : 'dashboard:manage_page.title')}
        </h1>
        {locked ? (
          <div className="flex items-center gap-2 text-sm text-yellow-400">
            <Lock className="w-4 h-4 shrink-0" />
            <span data-testid="manage-docs-locked-note">
              {t('dashboard:manage_page.locked_subtitle', { max: maxDocs })}
            </span>
          </div>
        ) : (
          <p className="text-sm text-[#a0a0a0]" data-testid="manage-docs-subtitle">{subtitle}</p>
        )}
        {!locked && (
          <p className="text-xs font-medium text-[#FF6B00]" data-testid="manage-docs-counter">
            {t('dashboard:manage_page.counter', { count: selected.length, max: maxDocs })}
          </p>
        )}
      </div>

      {/* Reminder — readable, calm tone (slightly larger for legibility) */}
      <div className="flex items-start gap-2.5 text-sm text-[#cfcfcf] bg-[#161616] border border-[#262626] rounded-xl px-3.5 py-3 leading-relaxed">
        <FileText className="w-4 h-4 text-[#FF6B00] shrink-0 mt-0.5" />
        <span>{t('dashboard:manage_page.reminder', { max: maxDocs })}</span>
      </div>

      {/* Upgrade escape hatch — existing flow */}
      {!locked && (
        <Button
          onClick={() => navigate('/upgrade')}
          variant="outline"
          className="border-[#FF6B00]/40 text-[#FF6B00] hover:bg-[#FF6B00]/10 rounded-full text-xs min-h-[44px] sm:min-h-0 sm:h-9 px-4"
          data-testid="manage-docs-upgrade"
        >
          <Sparkles className="w-3.5 h-3.5 mr-1.5" /> {t('dashboard:manage_page.upgrade_all')}
        </Button>
      )}

      {/* Stay active = the user's picks + the ones kept automatically (fill / never-archived) */}
      <Group title={t('dashboard:manage_page.group_active')} count={chosenList.length + autoList.length} testid="manage-docs-active-group">
        {chosenList.map(doc => (
          <DocRow key={doc.id} doc={doc} selected automatic={false} locked={locked}
            disabled={false} onToggle={() => toggle(doc.id)} t={t} />
        ))}
        {autoList.map(doc => (
          <DocRow key={doc.id} doc={doc} selected={false} automatic locked={locked}
            disabled={false} onToggle={() => toggle(doc.id)} t={t} />
        ))}
        {chosenList.length + autoList.length === 0 && (
          <p className="text-xs text-[#555] px-1 py-2">-</p>
        )}
      </Group>

      {/* Will be locked = ONLY the docs the sweep really archives (omitted when none) */}
      {blockedList.length > 0 && (
        <Group title={t('dashboard:manage_page.group_blocked')} count={blockedList.length} testid="manage-docs-blocked-group">
          {blockedList.map(doc => (
            <DocRow key={doc.id} doc={doc} selected={false} automatic={false} locked={locked}
              disabled={!locked && atMax} onToggle={() => toggle(doc.id)} t={t} />
          ))}
        </Group>
      )}

      {saving && (
        <div className="flex items-center gap-2 text-xs text-[#6a6a6a]" aria-live="polite">
          <Loader2 className="w-3 h-3 animate-spin" /> …
        </div>
      )}
    </div>
  );
}

function BackHeader({ navigate, t }) {
  // Arrow + label are ONE clickable button (>=44px tap target) that navigates to the dashboard.
  return (
    <button
      type="button"
      onClick={() => navigate('/dashboard')}
      className="inline-flex items-center gap-2 min-h-[44px] -ml-2 px-2 rounded-lg text-sm text-[#6a6a6a] hover:text-white transition-colors"
      data-testid="manage-docs-back"
    >
      <ArrowLeft className="w-5 h-5 shrink-0" />
      {t('dashboard:manage_page.back_to_dashboard')}
    </button>
  );
}

function Group({ title, count, testid, children }) {
  return (
    <div className="space-y-2" data-testid={testid}>
      <h2 className="text-xs font-semibold uppercase tracking-wide text-[#6a6a6a]">
        {title}, {count}
      </h2>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function DocRow({ doc, selected, automatic, locked, disabled, onToggle, t }) {
  // #154 — EN FILA. Misma regla y misma precedencia que la tarjeta del Panel
  // (Dashboard.js): en procesamiento y SIN `processing_started_at` (ausente o nulo) el
  // documento todavia espera turno en el semaforo de pipelines, y la rama de la fila GANA.
  // El sello llega en la respuesta de keep-selection desde #154
  // (services/downgrade_notice.py); antes esa proyeccion no lo pedia y esta pantalla no
  // podia distinguir las dos cosas. Aqui no hay barra que apagar: la pantalla es un
  // selector de que documentos se quedan activos, no una de progreso.
  const statusKey = doc.status === 'processing'
      ? (doc.processing_started_at ? 'processing' : 'queued')
    : doc.status === 'error' ? 'error' : 'ready';
  const interactive = !locked && !disabled;
  // The WHOLE card is the toggle target (not just the circle). >=44px tap target on mobile.
  // automatic rows get a faint orange tint (stays active, differentiated from blocked/neutral).
  const base = `w-full flex items-center gap-3 rounded-xl border px-3 py-3 min-h-[44px] text-left transition-colors ${
    selected ? 'border-[#FF6B00]/40 bg-[#FF6B00]/[0.05]'
      : automatic ? 'border-[#FF6B00]/20 bg-[#FF6B00]/[0.02]'
      : 'border-[#262626] bg-[#141414]'
  } ${disabled && !selected ? 'opacity-40' : ''}`;
  // The orange check stays as the VISUAL toggle indicator; it no longer handles its own click.
  const inner = (
    <>
      <span
        aria-hidden="true"
        className={`shrink-0 flex items-center justify-center w-7 h-7 rounded-full border transition-colors ${
          selected ? 'bg-[#FF6B00] border-[#FF6B00] text-white' : 'border-[#3a3a3a] text-transparent'
        }`}
      >
        <Check className="w-4 h-4" />
      </span>
      <span className="flex-1 min-w-0 text-sm text-white truncate" title={doc.filename}>
        {doc.filename}
      </span>
      {/* Informative marker: only "kept automatically" (no lock). The "Will be locked" group
          header already communicates the blocked state, so no per-card red badge. */}
      {automatic && (
        <Badge variant="outline" className="text-[10px] shrink-0 bg-[#FF6B00]/10 text-[#FF6B00] border-[#FF6B00]/25" data-testid="auto-kept-badge">
          {t('dashboard:manage_page.auto_kept_badge')}
        </Badge>
      )}
      {/* #154: texto y tono salen de la MISMA clave derivada — si se separaran, un
          documento en fila podria decir una cosa y pintarse de otra. */}
      <Badge variant="outline" className={`text-[10px] shrink-0 ${statusStyle(statusKey)}`}>
        {t(`dashboard:doc_card.status.${statusKey}`)}
      </Badge>
    </>
  );

  if (interactive) {
    return (
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={selected}
        className={`${base} cursor-pointer hover:border-[#FF6B00]/60`}
        data-testid={`manage-doc-${doc.id}`}
      >
        {inner}
      </button>
    );
  }
  return (
    <div className={base} data-testid={`manage-doc-${doc.id}`}>
      {inner}
    </div>
  );
}
